import {
  AddButton,
  FormLabel,
  RequiredIndicator,
  CancelButton,
} from "assets/js/Styles";
import CustomSelect from "components/customs/dropdown";
import FormGenerator from "components/customs/form-generator/input";
import { VIEW_ACTION } from "constants/actions";
import {
  TESDA_COURSE_PROGRAMS,
  TESDA_PROGRAM_STEP_ID,
  TESDA_PROGRAM_STEP_TOOLKIT_COST,
} from "constants/agency";
import { addProjectDropdownStyles } from "constants/style";
import { ErrorMessage, Field, Formik, Form } from "formik";
import React from "react";
import { Col, Row, Spinner } from "reactstrap";
import classNames from "classnames";
import styled from "styled-components";
import * as Yup from "yup";
import { useNavigate } from "react-router";

const FormColRight = styled.div`
  flex: 0 0 auto;
  width: 100%;

  @media (min-width: 768px) {
    flex: 0 0 auto;
    width: 50%;
  }

  .dropzone-container {
    width: auto;
    height: 65px;
    border: 1px dashed #a3a3a3;

    .upload-files {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  }
`;

const RowSection = styled(Row)`
  margin-top: 1.5rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
  margin-left: 0;
  margin-right: 0;
`;

const FormSection = styled.div`
  margin-top: 4rem;
`;

const StyledAddButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.buttonFormBlue};
  width: 130px;
`;

const StyledCancelButton = styled(CancelButton)`
  width: 130px;
`;

const BtnSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 3rem;
  padding-bottom: 4rem;
  gap: 16px;
`;

const validationSchema = Yup.object().shape({
  projectId: Yup.object().required("Required"),
  courseCode: Yup.number()
    .required("Required")
    .min(0.01, "Course Code cannot be empty or zero"),
  tesdaQualificationId: Yup.string().required("Required"),
  name: Yup.string().required("Required"),
  trainingCost: Yup.number()
    .required("Required")
    .min(0.01, "Training Cost cannot be empty or zero"),
  assessmentCost: Yup.number()
    .required("Required")
    .min(0.01, "Assessment Cost cannot be empty or zero"),
  trainingSupportFund: Yup.number()
    .required("Required")
    .min(0.01, "Training Support Fund cannot be empty or zero"),
  sectorId: Yup.object().required("Required"),
  toolKitCost: Yup.number().when("projectId", {
    is: (projectId) => projectId?.value === TESDA_PROGRAM_STEP_ID,
    then: (schema) =>
      schema
        .required("Required")
        .min(0.01, "Tool Kit Cost cannot be empty or zero"),
    otherwise: (schema) => schema.nullable(),
  }),
});

const TVICourseFormSection = ({
  form,
  action,
  isLoading,
  sectorOptions,
  handleSubmit,
}) => {
  const navigate = useNavigate();

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={form}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, handleChange }) => (
        <Form>
          <FormSection>
            <Field name="projectId">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Project Type"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the TESDA project type of the course"}</p>
                  </Col>
                  <FormColRight>
                    <CustomSelect
                      placeholder="Project Type"
                      name="projectId"
                      options={TESDA_COURSE_PROGRAMS}
                      styles={addProjectDropdownStyles}
                      value={values.projectId}
                      className={classNames({
                        "is-invalid": errors?.projectId && touched?.projectId,
                      })}
                      onChange={(option) => {
                        setFieldValue("projectId", option);
                      }}
                    />
                    <ErrorMessage name="projectId">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>

            <Field name="courseCode">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Course Code"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the course code"}</p>
                  </Col>
                  <FormColRight>
                    <FormGenerator
                      type="number"
                      name="courseCode"
                      placeholder="Course Code"
                      value={values.courseCode}
                      isDisabled={action === VIEW_ACTION}
                      thousandSeparator=""
                      className={classNames({
                        "is-invalid": errors?.courseCode && touched?.courseCode,
                      })}
                      onValueChange={(value) => {
                        const { floatValue } = value;
                        setFieldValue("courseCode", floatValue);
                      }}
                    />
                    <ErrorMessage name="courseCode">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>

            <Field name="tesdaQualificationId">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"TESDA Qualification ID"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the TESDA qualification ID"}</p>
                  </Col>
                  <FormColRight>
                    <FormGenerator
                      type="text"
                      name="tesdaQualificationId"
                      placeholder="TESDA Qualification ID"
                      value={values.tesdaQualificationId}
                      isDisabled={action === VIEW_ACTION}
                      className={classNames({
                        "is-invalid":
                          errors?.tesdaQualificationId &&
                          touched?.tesdaQualificationId,
                      })}
                      onChange={handleChange}
                    />
                    <ErrorMessage name="tesdaQualificationId">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>

            <Field name="name">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Course Name"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the name of the course"}</p>
                  </Col>
                  <FormColRight>
                    <FormGenerator
                      type="text"
                      name="name"
                      placeholder="Course Name"
                      value={values.name}
                      isDisabled={action === VIEW_ACTION}
                      className={classNames({
                        "is-invalid": errors?.name && touched?.name,
                      })}
                      onChange={handleChange}
                    />
                    <ErrorMessage name="name">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>

            <Field name="trainingCost">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Training Cost Per Slot"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the training cost per slot"}</p>
                  </Col>
                  <FormColRight>
                    <FormGenerator
                      type="number"
                      name="trainingCost"
                      placeholder="Training Cost Per Slot"
                      value={values.trainingCost}
                      decimalSeparator="."
                      thousandSeparator=","
                      className={classNames({
                        "is-invalid":
                          errors?.trainingCost && touched?.trainingCost,
                      })}
                      onValueChange={(value) => {
                        const { floatValue } = value;
                        setFieldValue("trainingCost", floatValue);
                      }}
                    />
                    <ErrorMessage name="trainingCost">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>

            <Field name="assessmentCost">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Assessment Cost Per Slot"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the assessment cost per slot"}</p>
                  </Col>
                  <FormColRight>
                    <FormGenerator
                      type="number"
                      name="assessmentCost"
                      placeholder="Assessment Cost Per Slot"
                      value={values.assessmentCost}
                      decimalSeparator="."
                      thousandSeparator=","
                      className={classNames({
                        "is-invalid":
                          errors?.assessmentCost && touched?.assessmentCost,
                      })}
                      onValueChange={(value) => {
                        const { floatValue } = value;
                        setFieldValue("assessmentCost", floatValue);
                      }}
                    />
                    <ErrorMessage name="assessmentCost">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>

            <Field name="trainingSupportFund">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Training Support Fund Per Slot"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the training support fund per slot"}</p>
                  </Col>
                  <FormColRight>
                    <FormGenerator
                      type="number"
                      name="trainingSupportFund"
                      placeholder="Training Support Fund Per Slot"
                      value={values.trainingSupportFund}
                      decimalSeparator="."
                      thousandSeparator=","
                      className={classNames({
                        "is-invalid":
                          errors?.trainingSupportFund &&
                          touched?.trainingSupportFund,
                      })}
                      onValueChange={(value) => {
                        const { floatValue } = value;
                        setFieldValue("trainingSupportFund", floatValue);
                      }}
                    />
                    <ErrorMessage name="trainingSupportFund">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>

            {values.projectId?.value === TESDA_PROGRAM_STEP_ID && (
              <>
                <Field name="toolKitCost">
                  {({ form: { touched, errors } }) => (
                    <RowSection>
                      <Col
                        xs="12"
                        md="6"
                      >
                        <FormLabel>
                          {"Toolkit Cost"}
                          {RequiredIndicator}
                        </FormLabel>
                        <p>{"Provide the toolkit cost per slot"}</p>
                      </Col>
                      <FormColRight>
                        <FormGenerator
                          type="number"
                          name="toolKitCost"
                          placeholder="Toolkit Cost Per Slot"
                          value={values.toolKitCost}
                          decimalSeparator="."
                          thousandSeparator=","
                          className={classNames({
                            "is-invalid":
                              errors?.toolKitCost && touched?.toolKitCost,
                          })}
                          onValueChange={(value) => {
                            const { floatValue } = value;
                            setFieldValue("toolKitCost", floatValue);
                          }}
                        />
                        <ErrorMessage name="toolKitCost">
                          {(msg) => (
                            <small className="field-error text-danger">
                              {msg}
                            </small>
                          )}
                        </ErrorMessage>
                      </FormColRight>
                    </RowSection>
                  )}
                </Field>

                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Entrep Fee"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Fixed cost for the toolkit"}</p>
                  </Col>
                  <FormColRight>
                    <FormGenerator
                      type="number"
                      name="entrepFee"
                      placeholder="Entrep Fee"
                      value={TESDA_PROGRAM_STEP_TOOLKIT_COST}
                      decimalSeparator="."
                      thousandSeparator=","
                      readOnly
                    />
                  </FormColRight>
                </RowSection>
              </>
            )}

            <Field name="sectorId">
              {({ form: { touched, errors } }) => (
                <RowSection>
                  <Col
                    xs="12"
                    md="6"
                  >
                    <FormLabel>
                      {"Sector"}
                      {RequiredIndicator}
                    </FormLabel>
                    <p>{"Provide the sector of the course"}</p>
                  </Col>
                  <FormColRight>
                    <CustomSelect
                      placeholder="Sector"
                      name="sectorId"
                      styles={addProjectDropdownStyles}
                      options={sectorOptions}
                      value={values.sectorId}
                      className={classNames({
                        "is-invalid": errors?.sectorId && touched?.sectorId,
                      })}
                      onChange={(option) => {
                        setFieldValue("sectorId", option);
                      }}
                      filterOption={(option, searchValue) =>
                        option.label
                          .toLowerCase()
                          .includes(searchValue?.toLowerCase())
                      }
                    />
                    <ErrorMessage name="sectorId">
                      {(msg) => (
                        <small className="field-error text-danger">{msg}</small>
                      )}
                    </ErrorMessage>
                  </FormColRight>
                </RowSection>
              )}
            </Field>
          </FormSection>
          <BtnSection>
            <StyledCancelButton
              outline
              type="button"
              disabled={isLoading}
              onClick={() => navigate(-1)}
            >
              {"Back"}
            </StyledCancelButton>

            <StyledAddButton
              color="primary"
              type="submit"
              disabled={isLoading || action === VIEW_ACTION}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Save"
              )}
            </StyledAddButton>
          </BtnSection>
        </Form>
      )}
    </Formik>
  );
};

export default TVICourseFormSection;
